import { Trash2, X, ChevronRight } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

// One guarded-item row (dark theme — lives on the Shield Dashboard + inside the modal).
export function GuardRow({ g, index, onOpen, onDelete, idPrefix = "guard" }) {
  const { row, item, avoid } = g;
  const name = item.title || row.label || row.query;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(g)}
      data-testid={`${idPrefix}-row-${index}`}
      className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-3 transition-colors hover:border-brand-orange/40 hover:bg-white/10"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white" data-testid={`${idPrefix}-name-${index}`}>{name}</p>
        {item.store && (
          <span className="mt-0.5 inline-block max-w-full truncate rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-zinc-300" data-testid={`${idPrefix}-store-${index}`}>{item.store}</span>
        )}
      </div>
      <span
        data-testid={`${idPrefix}-verdict-${index}`}
        className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] ${avoid ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}
      >
        {avoid ? "🛑 Avoid" : "🟢 Buy"}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(g); }}
        data-testid={`${idPrefix}-delete-${index}`}
        aria-label="Delete guard"
        className="shrink-0 rounded-lg p-2 text-zinc-500 transition-colors hover:bg-white/10 hover:text-red-400"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// Full scrollable list of every Active Price Guard — opened from the "View all" button
// so the dashboard itself never turns into an endless scroll.
export function PriceGuardsModal({ open, onClose, guards, onOpen, onDelete }) {
  useBodyScrollLock(open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="guards-modal" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-t-3xl bg-zinc-900 shadow-2xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-white/10 p-5">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-white">🛡️ Active Price Guards</h3>
            <p className="text-xs text-zinc-400" data-testid="guards-modal-count">{guards.length} item{guards.length === 1 ? "" : "s"} under your Shield</p>
          </div>
          <button onClick={onClose} data-testid="guards-modal-close" aria-label="Close" className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
          {guards.length === 0 ? (
            <p className="py-8 text-center text-sm text-zinc-500" data-testid="guards-modal-empty">No guards left — run a search to start tracking prices.</p>
          ) : (
            guards.map((g, i) => (
              <GuardRow key={g.row.tx_id || i} g={g} index={i} onOpen={onOpen} onDelete={onDelete} idPrefix="gm" />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// "Are you sure?" confirmation before a guard (and its shared Profile-history record) is deleted.
export function ConfirmDeleteDialog({ open, name, onCancel, onConfirm }) {
  useBodyScrollLock(open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="guard-delete-confirm" onClick={onCancel}>
      <div className="w-full max-w-xs rounded-2xl bg-white p-6 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <Trash2 className="h-6 w-6 text-red-500" />
        </div>
        <h3 className="mt-4 text-base font-bold text-gray-900">Delete this item?</h3>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          <span className="mb-1 block break-all line-clamp-2 font-semibold text-gray-900" title={name || undefined}>
            {name ? `"${name}"` : "This item"}
          </span>
          will be removed from your Shield Dashboard and your Profile history. This can't be undone.
        </p>
        <div className="mt-5 flex gap-3">
          <button onClick={onCancel} data-testid="guard-delete-cancel" className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={onConfirm} data-testid="guard-delete-confirm-btn" className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-600">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
